import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import Confetti from 'react-confetti';

/**
 * Resuelve la URL absoluta para recursos multimedia cargados dinámicamente.
 * 
 * @param {string} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL completa formateada.
 */
const getFullAssetUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/')
        .replace('/api/', '/')
        .replace('/api', '/');
    const cleanBase = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`;
    return `${cleanBase}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * COMPONENTE: PERSONAL QUICK REACTION OVERLAY (DESIGN / CHILD VISUAL)
 * ------------------------------------------------------------------
 * Este componente maneja la presentación visual del Overlay broadcast para
 * la dinámica "Reacción Rápida Personal" (HU-05).
 * 
 * Cumple con:
 * - Regla Zero-Canvas: no renderiza nada si está en estado 'idle'.
 * - Coordenadas y calibración visual para resoluciones 1536px y 1920px.
 * - Modo 'invitation': Renderiza cintillo + QRs limpios (sin textos HTML encimados).
 * - Modo 'winner': Renderiza cintillo + tarjeta de Ganador Actual con tiempo en ms,
 *   insignia oficial y confeti reactivo.
 * - Cero PII: No expone datos sensibles de participantes.
 * ------------------------------------------------------------------
 * 
 * @component
 * @param {Object} props
 * @param {string} props.flowState - Estado del flujo ('idle' | 'entering' | 'visible' | 'fadingOut').
 * @param {string} [props.overlayMode='invitation'] - Modo activo ('invitation' | 'winner').
 * @param {number} [props.qrCount=1] - Cantidad de códigos QR (1 o 2).
 * @param {number} [props.offsetY=0] - Desplazamiento vertical en píxeles.
 * @param {string} [props.banner1536Url=''] - URL de imagen de fondo 1536x180 px.
 * @param {string} [props.banner1920Url=''] - URL de imagen de fondo 1920x180 px.
 * @param {string} [props.qrCenterLogoUrl=''] - URL del logotipo central en QR.
 * @param {Object} [props.winnerData=null] - Datos del ganador actual ({ participantName, reactionTimeMs, totalTimeMs, badgeName }).
 * @param {string} [props.roomId=''] - Identificador opcional de sala.
 */
export function PersonalQuickReaction({
    flowState = 'idle',
    overlayMode = 'invitation',
    qrCount = 1,
    offsetY = 0,
    banner1536Url = '',
    banner1920Url = '',
    qrCenterLogoUrl = '',
    winnerData = null,
    roomId = ''
}) {
    // Dimensiones de ventana para confeti
    const [windowDimensions, setWindowDimensions] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 1920,
        height: typeof window !== 'undefined' ? window.innerHeight : 1080
    });

    useEffect(() => {
        const handleResize = () => {
            if (typeof window !== 'undefined') {
                setWindowDimensions({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleResize);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleResize);
            }
        };
    }, []);

    // REGLA ZERO-CANVAS: No renderizar nada si está en estado 'idle'
    if (flowState === 'idle') return null;

    // URL fija del QR móvil aficionado (SIN ZONA, SIN ROOMID)
    const qrValue = typeof window !== 'undefined'
        ? `${window.location.origin}/psl-qreaction?s=obs`
        : '';

    const mdSrc = banner1536Url
        ? getFullAssetUrl(banner1536Url)
        : '/img/overlays/rubaquiz/cintillo-ruba-1536-x-180-px.png';

    const lgSrc = banner1920Url
        ? getFullAssetUrl(banner1920Url)
        : '/img/overlays/rubaquiz/cintillo-ruba-1920-x-180-px.png';

    const centerLogo = qrCenterLogoUrl
        ? getFullAssetUrl(qrCenterLogoUrl)
        : '/img/overlays/rubaquiz/interaktia-transparent.png';

    const isDoubleQr = Number(qrCount) === 2;
    const isWinnerMode = String(overlayMode).toLowerCase() === 'winner';

    const qrImageSettings = {
        src: centerLogo,
        x: undefined,
        y: undefined,
        height: 52,
        width: 52,
        excavate: true
    };

    // Datos del ganador formateados
    const winnerName = winnerData?.participantName || winnerData?.name || 'Aficionado Campeón';
    const winnerMs = winnerData?.reactionTimeMs !== undefined ? winnerData.reactionTimeMs
        : (winnerData?.totalTimeMs !== undefined ? winnerData.totalTimeMs : 0);
    const winnerBadge = winnerData?.badgeName || 'Pistolero Veloz';

    return (
        <>
            {/* Celebración con Confeti en modo Ganador */}
            {isWinnerMode && winnerData && (
                <Confetti
                    width={windowDimensions.width}
                    height={windowDimensions.height}
                    numberOfPieces={160}
                    recycle={true}
                    gravity={0.14}
                    colors={['#ffd700', '#00ffcc', '#ff007f', '#ffffff', '#00d2ff', '#ff8c00']}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        zIndex: 9998,
                        pointerEvents: 'none'
                    }}
                />
            )}

            <div
                className="personal-quick-reaction-overlay"
                style={{ bottom: `${offsetY}px` }}
            >
                <div className={`personal-quick-reaction-overlay__banner personal-quick-reaction-overlay__banner--${flowState}`}>
                    {/* Banners 1536px y 1920px */}
                    <img
                        src={mdSrc}
                        className="personal-quick-reaction-overlay__banner__Image--Md"
                        alt="Reacción Rápida Personal"
                    />
                    <img
                        src={lgSrc}
                        className="personal-quick-reaction-overlay__banner__Image--Lg"
                        alt="Reacción Rápida Personal Lg"
                    />

                    {/* Códigos QR (1 QR en --single o 2 QRs en --left y --right) */}
                    {isDoubleQr ? (
                        <>
                            <div className="personal-quick-reaction-overlay__banner__QR-Overlay personal-quick-reaction-overlay__banner__QR-Overlay--left">
                                <QRCodeSVG
                                    value={qrValue}
                                    size={256}
                                    style={{ width: '92%', height: '92%', display: 'block' }}
                                    bgColor="#d9d9d9"
                                    fgColor="#000000"
                                    level="H"
                                    imageSettings={qrImageSettings}
                                />
                            </div>

                            <div className="personal-quick-reaction-overlay__banner__QR-Overlay personal-quick-reaction-overlay__banner__QR-Overlay--right">
                                <QRCodeSVG
                                    value={qrValue}
                                    size={256}
                                    style={{ width: '92%', height: '92%', display: 'block' }}
                                    bgColor="#d9d9d9"
                                    fgColor="#000000"
                                    level="H"
                                    imageSettings={qrImageSettings}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="personal-quick-reaction-overlay__banner__QR-Overlay personal-quick-reaction-overlay__banner__QR-Overlay--single">
                            <QRCodeSVG
                                value={qrValue}
                                size={256}
                                style={{ width: '92%', height: '92%', display: 'block' }}
                                bgColor="#d9d9d9"
                                fgColor="#000000"
                                level="H"
                                imageSettings={qrImageSettings}
                            />
                        </div>
                    )}

                    {/* Tarjeta de Ganador Actual en modo 'winner' */}
                    {isWinnerMode && (
                        <div className="personal-quick-reaction-overlay__banner__winner-card">
                            <div className="personal-quick-reaction-overlay__banner__winner-card__trophy">
                                <i className="bi bi-trophy-fill" />
                            </div>
                            <div className="personal-quick-reaction-overlay__banner__winner-card__info">
                                <span className="personal-quick-reaction-overlay__banner__winner-card__label">
                                    ¡Mejor Tiempo Actual!
                                </span>
                                <h3 className="personal-quick-reaction-overlay__banner__winner-card__name">
                                    {winnerName}
                                </h3>
                                <div className="personal-quick-reaction-overlay__banner__winner-card__stats">
                                    <span className="personal-quick-reaction-overlay__banner__winner-card__time">
                                        ⚡ {winnerMs} ms
                                    </span>
                                    <span className="personal-quick-reaction-overlay__banner__winner-card__badge">
                                        🎯 {winnerBadge}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

PersonalQuickReaction.propTypes = {
    flowState: PropTypes.oneOf(['idle', 'entering', 'visible', 'fadingOut']).isRequired,
    overlayMode: PropTypes.oneOf(['invitation', 'winner']),
    qrCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    offsetY: PropTypes.number,
    banner1536Url: PropTypes.string,
    banner1920Url: PropTypes.string,
    qrCenterLogoUrl: PropTypes.string,
    winnerData: PropTypes.shape({
        participantName: PropTypes.string,
        name: PropTypes.string,
        reactionTimeMs: PropTypes.number,
        totalTimeMs: PropTypes.number,
        badgeName: PropTypes.string
    }),
    roomId: PropTypes.string
};

export default PersonalQuickReaction;
